import { Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import {
  AriaAttributesSignalExcluded,
  HTMLAttributesSignalExcluded
} from '../utils/UNSAFE_attributeUtils';
import { TextProps, textInterpolations } from '../utils/UNSAFE_interpolations/text';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TextFieldInputVariantOptions } from '../UNSAFE_TextField/themes/TextFieldInputStyles.css';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { classNames } from '../utils/UNSAFE_classNames';
import { selectMobileFieldInputStyles } from './themes/SelectMobileFieldInputStyles.css';
import { isInputPlaceholderShown } from '../UNSAFE_TextField/TextFieldUtils';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { Text } from '../UNSAFE_Text';
import { useAccessibleContext } from '../hooks/UNSAFE_useAccessibleContext';
import { merge } from '../utils/UNSAFE_stringUtils';
import { TextFieldInputRedwoodTheme } from '../UNSAFE_TextField/themes/redwood/TextFieldInputTheme';

type AriaProps = Pick<
  AriaAttributesSignalExcluded,
  | 'aria-controls'
  | 'aria-describedby'
  | 'aria-expanded'
  | 'aria-invalid'
  | 'aria-label'
  | 'aria-labelledby'
>;

type EventProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'onBlur' | 'onFocus'>;

type Props = AriaProps &
  EventProps &
  TextProps & {
    displayValue?: string;
    hasEmptyLabel?: boolean;
    hasInsideLabel?: boolean;
    isRequired?: boolean;
    placeholder?: string;
    variant?: 'default' | 'embedded';
  };

const interpolations = [...Object.values(textInterpolations)];
const styleInterpolations = mergeInterpolations<Props>(interpolations);

/**
 * This component is used for rendering the main field in mobile phones. This is a bit different from the
 * normal main field (TextFieldInput) that we use for desktop & tablet devices in the way that
 * a user will not be able to edit the value of this field. But, one can still have other functionalities
 * of having a placeholder, tab in & out of it, and other such functionalities of an enabled field.
 *
 * This also differs from ReadonlyTextFieldInput in the following ways:
 * 1. This will be rendered as div as well, but has a role of combobox set on it
 * 2. This will not be read as readonly input by the screen readers
 * 3. This has the stylings much similar to the TextFieldInput component
 * 4. This supports the use of placeholder and shows it when there is no value present
 * 5. This supports having the variant property and styles accordingly
 *
 * As such, this is very similar to the TextFieldInput except that this does not allow user input.
 * Note: we cannot use TextFieldInput with readonly set on it; even though it might provide us the
 * behavior we need, it will make the field as readonly and screen readers read the same about this
 * field. This will be an incorrect representation contextually as one can still edit the value by opening
 * the dropdown and changing the selection.
 */
export const SelectMobileFieldInput = forwardRef(
  (
    {
      'aria-controls': ariaControls,
      'aria-describedby': ariaDescribedBy,
      'aria-expanded': ariaExpanded,
      'aria-invalid': ariaInvalid,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      displayValue,
      hasEmptyLabel,
      hasInsideLabel = false,
      isRequired,
      onBlur,
      onFocus,
      placeholder,
      textAlign,
      variant = 'default'
    }: Props,
    ref?: Ref<HTMLDivElement>
  ) => {
    // consume required contexts
    const { isDisabled, isFocused, hasValue = false } = useFormFieldContext();
    const { isTabbable, tabbableModeProps } = useTabbableMode();

    // setup properties
    const myPlaceholder = isInputPlaceholderShown(hasInsideLabel, hasValue, isFocused)
      ? placeholder
      : undefined;

    // Add unsafe API for specifying an external label which is added to ariaLabelledBy on the input,
    // only when labelEdge is 'none' and label is ''. Since ariaLabelledBy takes precedence over all other
    // kinds of labels, this helps to ensure we don't override a meaningful label.
    const { UNSAFE_ariaLabelledBy } = useAccessibleContext();
    const mergedAriaLabelledBy = hasEmptyLabel
      ? merge([ariaLabelledBy, UNSAFE_ariaLabelledBy])
      : ariaLabelledBy;

    // setup style classes
    const { class: styleInterpolationClasses } = styleInterpolations({ textAlign });
    const { classes: themeClasses } = useComponentTheme<TextFieldInputVariantOptions>(
      TextFieldInputRedwoodTheme,
      {
        type: 'notPassword',
        styleVariant: variant,
        textarea: 'notTextArea',
        input: 'isInput',
        prefix: 'noPrefix',
        suffix: 'noSuffix',
        startContent: 'noStartContent',
        endContent: 'hasEndContent',
        insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
        value: hasValue ? 'hasValue' : 'noValue',
        focused: isFocused ? 'isFocused' : 'notFocused',
        disabled: isDisabled ? 'isDisabled' : 'notDisabled'
      }
    );
    const rootDivStyleClasses = classNames([
      selectMobileFieldInputStyles.base,
      styleInterpolationClasses,
      themeClasses
    ]);

    return (
      <div
        aria-autocomplete="list"
        aria-controls={ariaControls}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        aria-labelledby={mergedAriaLabelledBy}
        aria-required={isRequired ? 'true' : undefined}
        class={rootDivStyleClasses}
        onBlur={onBlur}
        onFocus={onFocus}
        ref={ref}
        role="combobox"
        {...(isTabbable && tabbableModeProps)}>
        <div class={selectMobileFieldInputStyles.content}>
          <Text variant={displayValue ? 'inherit' : 'secondary'}>
            {displayValue || myPlaceholder}
          </Text>
        </div>
      </div>
    );
  }
);
